// App-wide layers (mockup core.js): screen-reader live region, one-line toast + progress toasts (070), the shared
// tooltip (076, [data-tip] and the hover preview of .term), and the "close everything floating" registry used when the
// route changes or a header menu opens. Mount <UiLayers /> once (the app root does it, tests get it from renderApp).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { termOf } from '../glossary';
import { useLang, useT } from '../i18n';
import { canHover, reduceMotion } from '../lib/media';
import { clamp, cls, firstSentence, mdPlain } from '../lib/util';
import { Icon, type IconName } from './Icon';

// ───────────────────────── floating things (popovers, menus, toggletips) ─────────────────────────
const closers = new Set<() => void>();
/** Register how to close an open floating element; returns the unregister function. */
export function registerFloating(close: () => void): () => void {
  closers.add(close);
  return () => {
    closers.delete(close);
  };
}
/** Close every open popover / menu / toggletip and the tooltip (route change, opening another menu). */
export function closeFloating(): void {
  [...closers].forEach((fn) => {
    try {
      fn();
    } catch {
      /* already closed */
    }
  });
  closers.clear();
  hideTip();
}

// ───────────────────────── live region ─────────────────────────
let liveEl: HTMLDivElement | null = null;
/** Say something politely to screen readers (#app-live). */
export function announce(text: string): void {
  if (liveEl) liveEl.textContent = text;
}

// ───────────────────────── top layer helpers ─────────────────────────
type PopoverCapable = HTMLElement & { showPopover?: () => void; hidePopover?: () => void };
/** Lift a popover="manual" element above open modal dialogs (top layer). No-op without the Popover API. */
export function raiseTop(el: HTMLElement | null): void {
  const p = el as PopoverCapable | null;
  if (!p || !p.isConnected || typeof p.showPopover !== 'function') return;
  try {
    if (p.matches(':popover-open')) p.hidePopover?.();
    p.showPopover();
  } catch {
    /* cannot lift: keep z-index */
  }
}
/** Where progress toasts live: inside the top-most modal dialog (outside it is inert), otherwise body. */
export function noticeHost(): HTMLElement {
  const open = Array.from(document.querySelectorAll('dialog[open]'));
  for (let i = open.length - 1; i >= 0; i--) {
    const d = open[i] as HTMLDialogElement;
    let modal = false;
    try {
      modal = d.matches(':modal');
    } catch {
      modal = d.hasAttribute('data-modal');
    }
    if (modal || d.hasAttribute('data-modal')) return d;
  }
  return document.body;
}

// ───────────────────────── toasts ─────────────────────────
export type ToastTone = 'ok' | 'warn' | 'danger' | 'info';
const TOAST_IC: Record<ToastTone, IconName> = { ok: 'check', warn: 'warn', danger: 'alert', info: 'info' };
const TOAST_MS = 2200;
const TOAST_DONE_MS = 3200;
const TOAST_STACK_MAX = 4;

interface ProgressCard {
  id: number;
  text: string;
  state: 'busy' | 'done';
  tone: ToastTone;
  out: boolean;
}
interface ToastState {
  line: { seq: number; text: string; tone: ToastTone } | null;
  cards: ProgressCard[];
  hostTick: number;
}
const useToastStore = create<ToastState>(() => ({ line: null, cards: [], hostTick: 0 }));
let cardSeq = 0;

export interface ProgressToast {
  update(text: string): ProgressToast;
  done(text?: string, tone?: ToastTone): ProgressToast;
  close(now?: boolean): ProgressToast;
}

function progressToast(text: string): ProgressToast {
  const id = ++cardSeq;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  const patch = (fn: (c: ProgressCard) => ProgressCard) =>
    useToastStore.setState((s) => ({ cards: s.cards.map((c) => (c.id === id ? fn(c) : c)) }));
  useToastStore.setState((s) => {
    let cards = s.cards;
    // too many: drop finished ones first, otherwise the oldest
    while (cards.length >= TOAST_STACK_MAX) {
      const victim = cards.find((c) => c.state === 'done') ?? cards[0];
      cards = cards.filter((c) => c !== victim);
    }
    return { cards: [...cards, { id, text, state: 'busy', tone: 'ok', out: false }] };
  });
  announce(text);
  const api: ProgressToast = {
    update(t) {
      if (!closed && t) patch((c) => ({ ...c, text: t }));
      return api;
    },
    done(t, tone = 'ok') {
      if (closed) return api;
      patch((c) => ({ ...c, state: 'done', tone, text: t || c.text }));
      if (t) announce(t);
      clearTimeout(timer);
      timer = setTimeout(() => api.close(), TOAST_DONE_MS);
      return api;
    },
    close(now) {
      if (closed) return api;
      closed = true;
      clearTimeout(timer);
      patch((c) => ({ ...c, out: true }));
      setTimeout(() => useToastStore.setState((s) => ({ cards: s.cards.filter((c) => c.id !== id) })), now || reduceMotion() ? 0 : 300);
      return api;
    },
  };
  return api;
}

export interface ToastFn {
  /** One-line toast at the bottom centre (2.2 s, the next one replaces it). */
  (text: string, tone?: ToastTone): void;
  /** Progress toast (070): stacks above the line toast; done() turns it into a check and it leaves after 3.2 s. */
  progress(text: string): ProgressToast;
}
let lineSeq = 0;
export const toast: ToastFn = Object.assign(
  (text: string, tone: ToastTone = 'ok') => {
    useToastStore.setState({ line: { seq: ++lineSeq, text, tone } });
  },
  { progress: progressToast },
);

/** Dialogs call this after opening/closing so notices move into / out of the top modal. */
export function raiseNotices(): void {
  useToastStore.setState((s) => ({ hostTick: s.hostTick + 1 }));
}

function ToastLine() {
  const line = useToastStore((s) => s.line);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !line) return;
    el.classList.remove('is-on');
    void el.offsetWidth; // restart the transition for back-to-back toasts
    raiseTop(el);
    el.classList.add('is-on');
    const tm = setTimeout(() => el.classList.remove('is-on'), TOAST_MS);
    return () => clearTimeout(tm);
  }, [line]);
  return (
    <div ref={ref} className={cls('toast', `toast--${line?.tone ?? 'ok'}`)} role="status" aria-live="polite" popover="manual">
      <Icon name={TOAST_IC[line?.tone ?? 'ok']} />
      <span>{line?.text ?? ''}</span>
    </div>
  );
}

function ToastStack() {
  const cards = useToastStore((s) => s.cards);
  const tick = useToastStore((s) => s.hostTick);
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setHost(noticeHost());
  }, [tick, cards.length]);
  useLayoutEffect(() => {
    const st = ref.current;
    document.documentElement.style.setProperty('--toast-lift', cards.length && st ? `${st.offsetHeight + 10}px` : '0px');
    if (cards.length) raiseTop(st);
  }, [cards, host]);
  if (!host) return null;
  return createPortal(
    <div ref={ref} className="toast-stack" role="region" aria-label={t('toast.stack')} hidden={!cards.length} popover="manual">
      {cards.map((c) => (
        <ToastCard key={c.id} card={c} />
      ))}
    </div>,
    host,
  );
}

function ToastCard({ card }: { card: ProgressCard }) {
  const t = useT();
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const close = () => {
    useToastStore.setState((s) => ({ cards: s.cards.map((c) => (c.id === card.id ? { ...c, out: true } : c)) }));
    setTimeout(() => useToastStore.setState((s) => ({ cards: s.cards.filter((c) => c.id !== card.id) })), reduceMotion() ? 0 : 300);
  };
  return (
    <div className={cls('toast-card', card.state === 'busy' ? 'is-busy' : ['is-done', `toast-card--${card.tone}`], entered && !card.out && 'is-in', card.out && 'is-out')}>
      <span className="toast-card__ic" aria-hidden="true">
        {card.state === 'busy' ? <i className="toast-card__spin" /> : <Icon name={TOAST_IC[card.tone]} />}
      </span>
      <span className="toast-card__text">{card.text}</span>
      <button className="toast-card__x" type="button" aria-label={t('toast.close')} data-tip={t('toast.close')} onClick={close}>
        <Icon name="x" />
      </button>
    </div>
  );
}

// ───────────────────────── tooltip (076): [data-tip] and the .term hover preview ─────────────────────────
let tipEl: HTMLDivElement | null = null;
let tipFor: HTMLElement | null = null;
let tipSetDesc = false;

export function hideTip(): void {
  if (!tipEl) return;
  tipEl.classList.remove('is-on');
  if (tipFor && tipSetDesc && tipFor.getAttribute('aria-describedby') === 'app-tip') tipFor.removeAttribute('aria-describedby');
  tipFor = null;
}

function placeTip(el: HTMLElement) {
  if (!tipEl) return;
  const r = el.getBoundingClientRect();
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const tw = tipEl.offsetWidth;
  const th = tipEl.offsetHeight;
  let left: number;
  let top: number;
  if (el.getAttribute('data-tip-side') === 'right') {
    left = r.right + 10;
    top = r.top + r.height / 2 - th / 2;
  } else {
    left = clamp(r.left + r.width / 2 - tw / 2, 8, Math.max(8, vw - tw - 8));
    top = r.top - th - 10;
    if (top < 8) top = r.bottom + 10;
  }
  tipEl.style.left = `${left + window.scrollX}px`;
  tipEl.style.top = `${top + window.scrollY}px`;
}

function labelHidden(el: Element): boolean {
  const lab = el.querySelector('.rail-help__text, .side-rail__label, .cmd-trigger__text');
  if (!lab) return false;
  const cs = getComputedStyle(lab);
  return cs.display === 'none' || cs.opacity === '0';
}
function railIsNarrow(): boolean {
  const shell = document.querySelector('.app-shell');
  if (!shell) return false;
  if (window.matchMedia?.('(max-width: 760px)').matches) return false;
  if (window.matchMedia?.('(max-width: 1099px)').matches) return !shell.classList.contains('is-rail-open');
  return shell.classList.contains('is-rail-narrow');
}

function TooltipLayer() {
  const t = useT();
  const lang = useLang();
  const ctx = useRef({ t, lang });
  ctx.current = { t, lang };

  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'tip';
    el.id = 'app-tip';
    el.setAttribute('role', 'tooltip');
    el.setAttribute('popover', 'manual');
    document.body.appendChild(el);
    tipEl = el;

    const content = (target: HTMLElement): Node | null => {
      if (target.classList.contains('term')) {
        if (target.getAttribute('aria-expanded') === 'true') return null;
        const g = termOf(target.getAttribute('data-term'));
        if (!g) return null;
        const box = document.createElement('span');
        box.className = 'tip__term';
        const b = document.createElement('b');
        b.textContent = ctx.current.lang === 'en' ? g.plainEn : g.plainKo;
        const d = document.createElement('span');
        d.setAttribute('data-term-source', 'def');
        d.textContent = firstSentence(mdPlain(ctx.current.lang === 'en' ? g.def.en : g.def.ko));
        const s = document.createElement('small');
        s.textContent = ctx.current.t('term.clickHint');
        box.append(b, d, s);
        return box;
      }
      const when = target.getAttribute('data-tip-when');
      if (when === 'rail-narrow' && !railIsNarrow() && !labelHidden(target)) return null;
      if (when === 'icon-only' && !labelHidden(target)) return null;
      const txt = target.getAttribute('data-tip');
      return txt ? document.createTextNode(txt) : null;
    };
    const show = (target: HTMLElement) => {
      const c = content(target);
      if (!c) {
        hideTip();
        return;
      }
      if (tipFor && tipFor !== target) hideTip();
      el.replaceChildren(c);
      raiseTop(el);
      el.classList.add('is-on');
      placeTip(target);
      if (tipFor !== target) {
        // do not describe a control with text already in its name (icon buttons, collapsed rail)
        const name = (target.getAttribute('aria-label') || target.textContent || '').replace(/\s+/g, ' ').trim();
        const tip = (el.textContent || '').trim();
        if (!target.hasAttribute('aria-describedby') && !(tip && name.includes(tip))) {
          target.setAttribute('aria-describedby', 'app-tip');
          tipSetDesc = true;
        } else tipSetDesc = false;
      }
      tipFor = target;
    };
    const SEL = '[data-tip], .term';
    let muted: Element | null = null;
    const closest = (e: Event): HTMLElement | null => (e.target instanceof Element ? (e.target.closest(SEL) as HTMLElement | null) : null);
    const onOver = (e: MouseEvent) => {
      let target = closest(e);
      if (target && target.classList.contains('term') && !canHover()) target = null;
      if (target && target === muted) return;
      if (target && target !== tipFor) show(target);
      else if (!target && tipFor && !tipFor.contains(document.activeElement)) hideTip();
    };
    const onOut = (e: MouseEvent) => {
      if (muted && !(e.relatedTarget instanceof Node && muted.contains(e.relatedTarget))) muted = null;
    };
    const onDown = (e: PointerEvent) => {
      const target = closest(e);
      if (target) muted = target;
      if (tipFor && e.target instanceof Node && tipFor.contains(e.target)) hideTip();
    };
    const focusVisible = (n: Element) => {
      try {
        return n.matches(':focus-visible');
      } catch {
        return true;
      }
    };
    const onFocusIn = (e: FocusEvent) => {
      const target = closest(e);
      if (target && !target.classList.contains('term') && e.target instanceof Element && focusVisible(e.target)) show(target);
      else hideTip();
    };
    const onFocusOut = (e: FocusEvent) => {
      if (tipFor && e.target === tipFor) hideTip();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hideTip();
    };
    const onScroll = () => {
      if (!tipFor) return;
      if (tipFor === document.activeElement || tipFor.contains(document.activeElement)) placeTip(tipFor);
      else hideTip();
    };
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      hideTip();
      el.remove();
      if (tipEl === el) tipEl = null;
    };
  }, []);
  return null;
}

/** Re-show the tooltip with new text when a control renames itself under the pointer/focus (theme, rail toggle). */
export function refreshTip(el: HTMLElement | null): void {
  if (el && tipFor === el && tipEl) {
    const txt = el.getAttribute('data-tip');
    if (txt) tipEl.textContent = txt;
    placeTip(el);
  }
}

// ───────────────────────── mount once ─────────────────────────
export function UiLayers({ children }: { children?: ReactNode }) {
  return (
    <>
      {children}
      <ToastLine />
      <ToastStack />
      <TooltipLayer />
      <div
        className="visually-hidden"
        aria-live="polite"
        id="app-live"
        ref={(el) => {
          liveEl = el;
        }}
      />
    </>
  );
}
